/* CostIntelligenceTab — LIVE food-cost % from eZee purchase data.
 *
 * Backed by /api/cost-intel/* endpoints (5 outlets, real revenue from
 * `ezee_sales_items`, real purchases from `ezee_purchase_records`).
 *
 * If eZee's purchase API isn't yet enabled on the hotel account, the
 * tab still renders revenue + a friendly "awaiting purchase API"
 * diagnostic so Dhiraj knows what to flip.
 *
 * Doctrine: <30% green · 30-35% amber · ≥35% red.
 */
import React, { useEffect, useState } from 'react';
import api from '../../lib/api';
import { AlertTriangle, TrendingDown, Percent, Store, ChefHat, Truck } from 'lucide-react';

const INR = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
const INR_SHORT = (n) => {
  const v = Number(n || 0);
  if (v >= 1e7) return `₹${(v / 1e7).toFixed(2)}Cr`;
  if (v >= 1e5) return `₹${(v / 1e5).toFixed(2)}L`;
  return INR(v);
};

const HEALTH_PALETTE = {
  green: { bg: '#ECFDF5', fg: '#065F46', label: 'Healthy' },
  amber: { bg: '#FFFBEB', fg: '#92400E', label: 'Watch' },
  red:   { bg: '#FEF2F2', fg: '#991B1B', label: 'Critical' },
  no_data: { bg: '#F3F4F6', fg: '#6B7280', label: 'No purchase data' },
};

const RANGE_OPTIONS = [
  { id: '7d', label: '7d' },
  { id: '30d', label: '30d' },
  { id: 'mtd', label: 'MTD' },
  { id: '90d', label: '90d' },
];

// Bundle 2.10 main · Hunk 4 — "specific month" picker for retrospective
// reporting (e.g. "what was Mar 2026 food cost?"). Generates the last
// 12 calendar months (excluding the current MTD which is covered by
// the chips) so it stays a clean dropdown.
function _generateMonthOptions() {
  const out = [];
  const now = new Date();
  // Start one month back so the current month is not duplicated with MTD
  for (let i = 1; i <= 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const ym = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const label = d.toLocaleString('en-IN', { month: 'short', year: 'numeric' });
    out.push({ id: ym, label });
  }
  return out;
}
const MONTH_OPTIONS = _generateMonthOptions();


export default function CostIntelligenceTab({ outletId }) {
  const [range, setRange] = useState('30d');
  const [summary, setSummary] = useState(null);
  const [byKitchen, setByKitchen] = useState(null);
  const [vendors, setVendors] = useState(null);
  const [trend, setTrend] = useState(null);
  const [loading, setLoading] = useState(true);
  const [meta, setMeta] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [s, k, v, t, m] = await Promise.all([
          api.get(`/cost-intel/${outletId}/summary?range=${range}`),
          api.get(`/cost-intel/${outletId}/by-kitchen?range=${range}`),
          api.get(`/cost-intel/${outletId}/vendors?range=${range}&limit=10`),
          api.get(`/cost-intel/${outletId}/daily-trend?range=${range}`),
          api.get(`/cost-intel/_meta`),
        ]);
        if (cancelled) return;
        setSummary(s.data);
        setByKitchen(k.data);
        setVendors(v.data);
        setTrend(t.data);
        setMeta(m.data);
      } catch (e) {
        console.error(e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [outletId, range]);

  if (loading) {
    return <div className="text-sm" style={{ color: '#6B7280' }}>Loading cost intelligence…</div>;
  }

  const pct = summary?.food_cost_pct || 0;
  const health = summary?.health || 'no_data';
  const palette = HEALTH_PALETTE[health];
  const noData = summary?.purchase_record_count === 0;

  return (
    <div className="space-y-4" data-testid="cost-intel">
      {/* Range chips */}
      <div className="flex items-center justify-between">
        <p className="text-[11px] uppercase tracking-wider" style={{ color: '#6B7280' }}>
          {summary?.outlet_name} · {summary?.from_date} → {summary?.to_date}
        </p>
        <div className="flex items-center gap-2">
          {RANGE_OPTIONS.map(o => (
            <button
              key={o.id}
              onClick={() => setRange(o.id)}
              data-testid={`cost-range-${o.id}`}
              className="px-3 py-1 text-[11px] rounded-full"
              style={{
                background: range === o.id ? '#1B2A4A' : 'transparent',
                color: range === o.id ? 'white' : '#6B7280',
                border: range === o.id ? 'none' : '1px solid #E5E1DC',
              }}
            >
              {o.label}
            </button>
          ))}
          {/* Specific calendar month picker — Bundle 2.10 main · Hunk 4 */}
          <select
            value={MONTH_OPTIONS.some(m => m.id === range) ? range : ''}
            onChange={(e) => e.target.value && setRange(e.target.value)}
            data-testid="cost-month-picker"
            className="px-2 py-1 text-[11px] rounded-full"
            style={{
              background: MONTH_OPTIONS.some(m => m.id === range) ? '#1B2A4A' : 'transparent',
              color: MONTH_OPTIONS.some(m => m.id === range) ? 'white' : '#6B7280',
              border: MONTH_OPTIONS.some(m => m.id === range) ? 'none' : '1px solid #E5E1DC',
              cursor: 'pointer',
            }}
            title="Pick a specific month for retrospective reporting"
          >
            <option value="">Month…</option>
            {MONTH_OPTIONS.map(m => (
              <option key={m.id} value={m.id}>{m.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Hero KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi
          label="Revenue (eZee live)"
          value={INR_SHORT(summary?.revenue)}
          sub={`Sales items source · ${summary?.from_date}`}
          icon={Store}
          testid="kpi-revenue"
        />
        <Kpi
          label="Purchases (vendor GRNs)"
          value={INR_SHORT(summary?.purchases_total)}
          sub={`${summary?.purchase_record_count || 0} record${summary?.purchase_record_count === 1 ? '' : 's'} · gross ${INR_SHORT(summary?.purchases_gross)}`}
          icon={Truck}
          testid="kpi-purchases"
        />
        <Kpi
          label="Food + Bev cost"
          value={`${pct}%`}
          sub={`${palette.label} · industry std (food+beverage / revenue)`}
          icon={Percent}
          accent={palette.fg}
          highlight
          testid="kpi-food-cost-pct"
        />
        <Kpi
          label="Δ vs prior period"
          value={summary?.delta_pct === 0
            ? '—'
            : `${summary?.delta_pct > 0 ? '+' : ''}${summary?.delta_pct}pp`}
          sub={`Was ${summary?.previous_period_pct || 0}% last period`}
          icon={TrendingDown}
          accent={summary?.delta_pct > 0 ? '#DC2626' : '#10B981'}
          testid="kpi-delta-pct"
        />
      </div>

      {/* Categorized split */}
      {summary?.by_category && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="cost-category-split">
          <SubKpi
            label="Food only"
            value={`${summary?.food_only_pct || 0}%`}
            amount={summary.by_category.food}
            sub={`${INR_SHORT(summary.by_category.food)} · ingredients`}
            tone="#10B981"
            testid="kpi-food-only"
          />
          <SubKpi
            label="Beverage"
            value={`${(((summary.by_category.beverage || 0) / (summary.revenue || 1)) * 100).toFixed(1)}%`}
            amount={summary.by_category.beverage}
            sub={`${INR_SHORT(summary.by_category.beverage)} · alcohol + drinks`}
            tone="#A0763C"
            testid="kpi-beverage"
          />
          <SubKpi
            label="Consumables"
            value={`${summary?.consumable_pct || 0}%`}
            amount={summary.by_category.consumable}
            sub={`${INR_SHORT(summary.by_category.consumable)} · gas, cleaning, packaging`}
            tone="#6B7280"
            testid="kpi-consumable"
          />
          <SubKpi
            label="Unclassified"
            value={`${(((summary.by_category.unknown || 0) / (summary.revenue || 1)) * 100).toFixed(1)}%`}
            amount={summary.by_category.unknown}
            sub={`${INR_SHORT(summary.by_category.unknown)} · review tagging`}
            tone="#9CA3AF"
            testid="kpi-unknown"
          />
        </div>
      )}

      {/* Health banner */}
      <div
        data-testid="cost-health-banner"
        className="rounded-xl px-4 py-3 text-[13px]"
        style={{ backgroundColor: palette.bg, color: palette.fg,
                  border: `1px solid ${palette.fg}33` }}
      >
        <div className="flex items-center gap-2">
          <AlertTriangle size={14} />
          <strong>{palette.label}.</strong>
          {health === 'green' && <span> Food cost % is well within industry benchmarks (target &lt;30%).</span>}
          {health === 'amber' && <span> Food cost % is in the watch zone (30-35%). Audit by kitchen below.</span>}
          {health === 'red'   && <span> Food cost % is above 35% — investigate top vendors and kitchens immediately.</span>}
          {health === 'no_data' && (
            <span>
              No vendor purchase records for this date range.
              {' '}
              The eZee F&amp;B Purchase ingest cron runs nightly @ 03:30 IST. If
              GRNs were posted into eZee Optimus they will appear in the next
              run. For an immediate refresh ask the platform team to trigger
              the admin ingest.
              {' '}
              Revenue continues to flow from sales-items.
            </span>
          )}
        </div>
      </div>

      {/* Daily trend mini-chart */}
      {trend?.series?.length > 0 && (
        <Card title="Daily revenue + purchases" testid="card-trend">
          <DailyTrendChart series={trend.series} />
          <p className="text-[11px] mt-2" style={{ color: '#6B7280' }}>
            Bars: revenue (navy) vs purchases (copper). The line tracks
            food-cost % per day where both numbers exist.
          </p>
        </Card>
      )}

      {/* Kitchen breakdown */}
      <Card
        title={`Spend by kitchen / item · ${byKitchen?.rows?.length || 0} cost centres`}
        testid="card-by-kitchen"
      >
        {(byKitchen?.rows || []).length === 0 ? (
          <div className="text-[12px]" style={{ color: '#6B7280' }}>
            No purchase records yet. Once Dhiraj enables the API, every
            item from every GRN gets attributed to its kitchen so you can
            spot which one is overspending.
          </div>
        ) : (
          <div className="space-y-2">
            {(byKitchen?.rows || []).slice(0, 12).map(r => (
              <div key={r.kitchen_or_item} className="flex items-center gap-3 text-[12px]">
                <ChefHat size={12} color="#9CA3AF" />
                <span className="flex-1 truncate font-medium" style={{ color: '#1B2A4A' }}>
                  {r.kitchen_or_item}
                </span>
                <span className="rounded-full h-2 w-32" style={{ backgroundColor: '#F3F4F6' }}>
                  <span className="block h-2 rounded-full" style={{
                    width: `${Math.min((r.pct_of_outlet_purchases || 0), 100)}%`,
                    backgroundColor: r.pct_of_outlet_purchases >= 30 ? '#DC2626' :
                                      r.pct_of_outlet_purchases >= 15 ? '#F59E0B' : '#10B981',
                  }} />
                </span>
                <span className="w-12 text-right" style={{ color: '#6B7280' }}>
                  {r.pct_of_outlet_purchases}%
                </span>
                <span className="w-24 text-right font-semibold tabular-nums">
                  {INR_SHORT(r.amount)}
                </span>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Top vendors */}
      <Card
        title={`Top vendors · ${vendors?.vendor_count || 0} vendors · ${INR_SHORT(vendors?.grand_total)}`}
        testid="card-vendors"
      >
        {(vendors?.rows || []).length === 0 ? (
          <div className="text-[12px]" style={{ color: '#6B7280' }}>
            Vendor data appears here once purchase records are ingested.
          </div>
        ) : (
          <table className="w-full text-[12px]">
            <thead>
              <tr style={{ color: '#9CA3AF' }}>
                <th className="text-left py-2 text-[10px] uppercase tracking-wider">Vendor</th>
                <th className="text-left py-2 text-[10px] uppercase tracking-wider">City</th>
                <th className="text-right py-2 text-[10px] uppercase tracking-wider">Records</th>
                <th className="text-right py-2 text-[10px] uppercase tracking-wider" style={{ color: '#10B981' }}>Food</th>
                <th className="text-right py-2 text-[10px] uppercase tracking-wider" style={{ color: '#A0763C' }}>Bev</th>
                <th className="text-right py-2 text-[10px] uppercase tracking-wider" style={{ color: '#6B7280' }}>Cons</th>
                <th className="text-right py-2 text-[10px] uppercase tracking-wider">Total</th>
                <th className="text-right py-2 text-[10px] uppercase tracking-wider">Share</th>
              </tr>
            </thead>
            <tbody>
              {vendors.rows.map((r, i) => (
                <tr key={i} style={{ borderTop: '1px solid #F3F4F6' }}>
                  <td className="py-2 font-medium" style={{ color: '#1B2A4A' }}>{r.vendor}</td>
                  <td className="py-2 text-[11px]" style={{ color: '#6B7280' }}>{(r.vendor_city || '').slice(0, 18) || '—'}</td>
                  <td className="py-2 text-right" style={{ color: '#6B7280' }}>{r.record_count}</td>
                  <td className="py-2 text-right tabular-nums" style={{ color: r.food > 0 ? '#10B981' : '#D1D5DB' }}>{r.food > 0 ? INR_SHORT(r.food) : '—'}</td>
                  <td className="py-2 text-right tabular-nums" style={{ color: r.beverage > 0 ? '#A0763C' : '#D1D5DB' }}>{r.beverage > 0 ? INR_SHORT(r.beverage) : '—'}</td>
                  <td className="py-2 text-right tabular-nums" style={{ color: r.consumable > 0 ? '#6B7280' : '#D1D5DB' }}>{r.consumable > 0 ? INR_SHORT(r.consumable) : '—'}</td>
                  <td className="py-2 text-right font-semibold tabular-nums">{INR_SHORT(r.total)}</td>
                  <td className="py-2 text-right" style={{ color: '#6B7280' }}>{r.pct_of_total}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}


function SubKpi({ label, value, sub, tone, testid }) {
  return (
    <div
      className="rounded-xl bg-white p-4"
      style={{ border: '1px solid #E5E1DC', borderLeft: `4px solid ${tone}` }}
      data-testid={testid}
    >
      <p className="text-[10px] uppercase tracking-wider" style={{ color: '#6B7280' }}>{label}</p>
      <p className="text-xl font-semibold mt-0.5" style={{ color: tone }}>{value}</p>
      <p className="text-[11px] mt-1" style={{ color: '#6B7280' }}>{sub}</p>
    </div>
  );
}


function Kpi({ label, value, sub, highlight, accent, icon: Icon, testid }) {
  const bg = highlight ? '#1B2A4A' : 'white';
  const fg = highlight ? 'white' : '#1B2A4A';
  const muted = highlight ? '#9DB5CC' : '#6B7280';
  return (
    <div
      className="rounded-xl p-4"
      style={{
        backgroundColor: bg, color: fg,
        border: highlight ? 'none' : '1px solid #E5E1DC',
        borderLeft: accent ? `4px solid ${accent}` : undefined,
      }}
      data-testid={testid}
    >
      <div className="flex items-center gap-1.5 mb-1">
        {Icon && <Icon size={12} color={muted} />}
        <p className="text-[10px] uppercase tracking-wider" style={{ color: muted }}>{label}</p>
      </div>
      <p className="text-2xl font-semibold">{value}</p>
      <p className="text-[11px] mt-1" style={{ color: muted }}>{sub}</p>
    </div>
  );
}


function Card({ title, children, testid }) {
  return (
    <div
      className="rounded-xl bg-white p-5"
      style={{ border: '1px solid #E5E1DC' }}
      data-testid={testid}
    >
      <h3 className="text-sm font-semibold mb-3" style={{ color: '#1B2A4A' }}>{title}</h3>
      {children}
    </div>
  );
}


function DailyTrendChart({ series }) {
  if (!series || series.length === 0) return null;
  const maxRev = Math.max(...series.map(d => d.revenue || 0), 1);
  const maxPur = Math.max(...series.map(d => d.purchases || 0), 1);
  const max = Math.max(maxRev, maxPur);
  return (
    <div className="flex items-end gap-1 h-32" data-testid="cost-trend-chart">
      {series.map((d) => {
        const revH = max > 0 ? (d.revenue / max) * 100 : 0;
        const purH = max > 0 ? (d.purchases / max) * 100 : 0;
        const tip = `${d.date}\nRevenue ${INR_SHORT(d.revenue)}\nPurchases ${INR_SHORT(d.purchases)}\nFood cost ${d.food_cost_pct}%`;
        return (
          <div key={d.date} className="flex-1 flex flex-col items-center gap-px h-full justify-end" title={tip}>
            <div className="w-full flex items-end gap-px h-full">
              <span className="flex-1 rounded-t-sm" style={{ height: `${revH}%`, backgroundColor: '#1B2A4A', minHeight: revH > 0 ? '2px' : '0' }} />
              <span className="flex-1 rounded-t-sm" style={{ height: `${purH}%`, backgroundColor: '#A0763C', minHeight: purH > 0 ? '2px' : '0' }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}
